import { useEffect, useRef, useState } from "react";

/** Animates a number from 0 to `target` over `duration` ms when element enters viewport */
export default function useCountUp(target, duration = 1800, suffix = "") {
  const [display, setDisplay] = useState("0" + suffix);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          const str = String(target);
          const simpleNumeric = str.match(/^([^0-9]*)(\d+(?:[.,]\d+)?)([^0-9]*)$/);
          if (!simpleNumeric || /[a-zA-ZğüşıöçĞÜŞİÖÇ]/.test(str)) {
            setDisplay(str);
            observer.disconnect();
            return;
          }
          const numericTarget = parseFloat(simpleNumeric[2].replace(",", "."));
          const prefix = simpleNumeric[1] ?? "";
          const rawSuffix = simpleNumeric[3] ?? suffix;
          const decimals = simpleNumeric[2].includes(".") || simpleNumeric[2].includes(",")
            ? (simpleNumeric[2].split(/[.,]/)[1]?.length ?? 0)
            : 0;
          const start = performance.now();
          const tick = (now) => {
            const elapsed = now - start;
            const progress = Math.min(elapsed / duration, 1);
            // ease-out cubic
            const eased = 1 - Math.pow(1 - progress, 3);
            const current = eased * numericTarget;
            setDisplay(prefix + current.toFixed(decimals) + rawSuffix);
            if (progress < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [target, duration, suffix]);

  return { ref, display };
}
